import { useEffect, useState } from "react";
import type { WorkedExampleBlock } from "../contract/types";
import { Icon } from "../components/Icon";
import { Tex, TexText } from "../components/Math";
import type { BlockViewProps } from "./types";

/**
 * Cálculo resuelto paso a paso.
 *
 * El stepper importa: en este curso el valor didáctico está en los pasos
 * intermedios (de dónde sale cada flujo, por qué esa tasa), no en el número
 * final. El resultado solo aparece cuando se llega al último paso, y siempre
 * queda a la vista una vez alcanzado.
 */
export function WorkedExample({ block }: BlockViewProps<WorkedExampleBlock>) {
  const steps = block.steps ?? [];
  const total = steps.length;
  const [index, setIndex] = useState(0);

  // Si el bloque se reemplaza por otro (mismo hueco, otro contenido), el
  // stepper vuelve al principio en lugar de quedarse en un paso inexistente.
  useEffect(() => {
    setIndex(0);
  }, [block.id]);

  if (total === 0) {
    return (
      <div className="atom atom--worked-example worked">
        <p className="worked__prompt">{block.prompt}</p>
        <p className="atom__empty">No hay pasos para mostrar.</p>
      </div>
    );
  }

  const current = steps[Math.min(index, total - 1)];
  const atEnd = index >= total - 1;

  return (
    <div className="atom atom--worked-example worked">
      <p className="worked__prompt">{block.prompt}</p>

      <div className="worked__stepper">
        <div className="worked__progress" role="group" aria-label="Pasos del cálculo">
          {steps.map((step, stepIndex) => (
            <button
              key={`${step.title}-${stepIndex}`}
              type="button"
              className={[
                "worked__dot",
                stepIndex === index ? "worked__dot--current" : "",
                stepIndex < index ? "worked__dot--done" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-label={`Paso ${stepIndex + 1}: ${step.title}`}
              aria-current={stepIndex === index ? "step" : undefined}
              onClick={() => setIndex(stepIndex)}
            />
          ))}
        </div>
        <span className="worked__counter u-nums">
          Paso {index + 1} de {total}
        </span>
      </div>

      <div className="worked__step">
        <h4 className="worked__step-title">{current?.title}</h4>
        <div className="worked__step-body">
          <TexText text={current?.body ?? ""} />
        </div>
        {current?.latex ? (
          <div className="worked__latex">
            <Tex latex={current.latex} display />
          </div>
        ) : null}
      </div>

      <div className="worked__nav">
        <button
          type="button"
          className="u-button"
          disabled={index === 0}
          onClick={() => setIndex((value) => Math.max(0, value - 1))}
        >
          <Icon name="chevron-left" size="0.875rem" />
          Anterior
        </button>
        <button
          type="button"
          className="u-button"
          disabled={atEnd}
          onClick={() => setIndex((value) => Math.min(total - 1, value + 1))}
        >
          Siguiente
          <Icon name="chevron-right" size="0.875rem" />
        </button>
      </div>

      {atEnd ? (
        <div className="worked__result">
          <span className="worked__result-label">{block.result_label}</span>
          <span className="worked__result-value">
            <span className="u-nums">{block.result_value}</span>
            {block.result_unit ? (
              <span className="worked__result-unit">{block.result_unit}</span>
            ) : null}
          </span>
        </div>
      ) : (
        <p className="worked__teaser">
          <Icon name="target" size="0.875rem" />
          El resultado aparece al llegar al último paso.
        </p>
      )}
    </div>
  );
}

export default WorkedExample;
